import React, { useState, useEffect, useCallback } from 'react';
import { getUsers, createUser, updateUser, getOrgTree } from '../api';

const C = {
  slate900:'#0f172a', slate700:'#334155', slate500:'#64748b', slate400:'#94a3b8',
  slate200:'#e2e8f0', slate100:'#f1f5f9', slate50:'#f8fafc',
  blue600:'#2563eb', blue500:'#3b82f6', blue50:'#eff6ff',
  green600:'#16a34a', green50:'#f0fdf4',
  amber600:'#d97706', amber50:'#fffbeb',
  rose600:'#dc2626', rose50:'#fef2f2',
  indigo:'#6366f1', white:'#fff',
};

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'vp', label: 'Vice President' },
  { value: 'construction_director', label: 'Construction Director' },
  { value: 'construction_manager', label: 'Construction Manager' },
  { value: 'pm', label: 'Project Manager' },
];

const roleLabel = (role) => {
  const found = ROLE_OPTIONS.find(r => r.value === role);
  return found ? found.label : role;
};

const roleColors = {
  admin: { bg: '#f3e8ff', fg: '#7c3aed' },
  vp: { bg: '#fef3c7', fg: '#b45309' },
  construction_director: { bg: '#dbeafe', fg: '#1d4ed8' },
  construction_manager: { bg: C.blue50, fg: C.blue600 },
  pm: { bg: C.green50, fg: C.green600 },
};

// Which roles can report to which
const VALID_REPORTS_TO = {
  vp: [],           // VP reports to no one (or admin in spirit)
  construction_director: ['vp'],
  construction_manager: ['vp', 'construction_director'],
  pm: ['construction_manager'],
};

const fmtDate = (d) => {
  if (!d) return '–';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

const UserManagementPage = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [orgTree, setOrgTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'org'

  // Form
  const [formEmail, setFormEmail] = useState('');
  const [formFirstName, setFormFirstName] = useState('');
  const [formLastName, setFormLastName] = useState('');
  const [formRole, setFormRole] = useState('pm');
  const [formPassword, setFormPassword] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [formReportsTo, setFormReportsTo] = useState('');
  const [formRegion, setFormRegion] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [userData, treeData] = await Promise.all([getUsers(), getOrgTree()]);
      setUsers(userData);
      setOrgTree(treeData);
    } catch (e) { console.error(e); setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setFormEmail(''); setFormFirstName(''); setFormLastName('');
    setFormRole('pm'); setFormPassword(''); setFormActive(true);
    setFormReportsTo(''); setFormRegion('');
    setShowForm(false); setEditingUser(null); setError('');
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormEmail(user.email);
    setFormFirstName(user.first_name);
    setFormLastName(user.last_name);
    setFormRole(user.role);
    setFormActive(!!user.is_active);
    setFormReportsTo(user.reports_to ? String(user.reports_to) : '');
    setFormRegion(user.region || '');
    setFormPassword('');
    setShowForm(true);
    setError('');
  };

  const handleCreate = () => {
    resetForm();
    setShowForm(true);
  };

  // Get valid managers for the selected role
  const getManagerOptions = () => {
    const validRoles = VALID_REPORTS_TO[formRole] || [];
    if (validRoles.length === 0) return [];
    return orgTree.filter(u => validRoles.includes(u.role) && u.id !== editingUser?.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setSubmitting(true);

    try {
      if (editingUser) {
        const data = {
          email: formEmail, first_name: formFirstName, last_name: formLastName,
          role: formRole, is_active: formActive,
          reports_to: formReportsTo ? parseInt(formReportsTo) : null,
          region: formRegion || null,
        };
        if (formPassword) data.password = formPassword;
        await updateUser(editingUser.id, data);
        setSuccess(`${formFirstName} ${formLastName} updated`);
      } else {
        if (!formPassword || formPassword.length < 6) {
          setError('Password must be at least 6 characters'); setSubmitting(false); return;
        }
        await createUser({
          email: formEmail, password: formPassword,
          first_name: formFirstName, last_name: formLastName,
          role: formRole,
          reports_to: formReportsTo ? parseInt(formReportsTo) : null,
          region: formRegion || null,
        });
        setSuccess(`${formFirstName} ${formLastName} created`);
      }
      resetForm();
      fetchData();
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  const card = { background: C.white, borderRadius: 16, border: `1px solid ${C.slate200}`, padding: '24px 28px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,.03)' };
  const inputStyle = {
    width: '100%', padding: '10px 14px', fontSize: 14, fontWeight: 500,
    border: `1px solid ${C.slate200}`, borderRadius: 10, backgroundColor: C.white,
    color: C.slate900, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: C.slate400, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 };
  const pill = (active) => ({
    padding: '8px 20px', borderRadius: 24, border: 'none',
    background: active ? C.indigo : 'transparent', color: active ? '#fff' : C.slate500,
    fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all .15s',
    boxShadow: active ? '0 2px 10px rgba(99,102,241,.3)' : 'none',
  });

  /* ── ORG CHART TREE RENDERER ── */
  const buildTree = () => {
    const map = {};
    orgTree.forEach(u => { map[u.id] = { ...u, children: [] }; });
    const roots = [];
    orgTree.forEach(u => {
      if (u.reports_to && map[u.reports_to]) {
        map[u.reports_to].children.push(map[u.id]);
      } else {
        roots.push(map[u.id]);
      }
    });
    // Sort by role rank then name
    const rankOrder = { admin: 0, vp: 1, construction_director: 2, construction_manager: 3, pm: 4 };
    const sort = (arr) => {
      arr.sort((a, b) => (rankOrder[a.role] || 9) - (rankOrder[b.role] || 9) || a.name.localeCompare(b.name));
      arr.forEach(n => sort(n.children));
    };
    sort(roots);
    return roots;
  };

  const OrgNode = ({ node, depth = 0 }) => {
    const rc = roleColors[node.role] || roleColors.pm;
    return (
      <div style={{ marginLeft: depth * 32 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
          borderRadius: 10, border: `1px solid ${C.slate200}`, backgroundColor: C.white,
          marginBottom: 6, transition: 'all .15s',
        }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = C.slate50}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = C.white}
        >
          {depth > 0 && (
            <span style={{ color: C.slate300, fontSize: 14, marginRight: 4 }}>└</span>
          )}
          <div style={{
            width: 34, height: 34, borderRadius: '50%', backgroundColor: rc.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, color: rc.fg, flexShrink: 0,
          }}>
            {node.name.split(' ').map(n => n[0]).join('')}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.slate900 }}>{node.name}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                backgroundColor: rc.bg, color: rc.fg, textTransform: 'uppercase', letterSpacing: .3,
              }}>{node.role_label}</span>
              {node.region && (
                <span style={{ fontSize: 11, color: C.slate400 }}>{node.region}</span>
              )}
            </div>
          </div>
          <button onClick={() => {
            const full = users.find(u => u.id === node.id);
            if (full) handleEdit(full);
          }} style={{
            fontSize: 11, fontWeight: 600, color: C.blue600, background: C.blue50,
            border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer',
          }}>Edit</button>
        </div>
        {node.children.map(child => (
          <OrgNode key={child.id} node={child} depth={depth + 1} />
        ))}
      </div>
    );
  };

  if (loading) return (
    <div style={{ padding: '80px 20px', textAlign: 'center' }}>
      <div style={{ width: 44, height: 44, border: `3px solid ${C.slate100}`, borderTopColor: C.indigo, borderRadius: '50%', margin: '0 auto 16px', animation: 'spin .7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ color: C.slate500, fontSize: 14, fontWeight: 500 }}>Loading users...</div>
    </div>
  );

  const tree = buildTree();
  const managerOptions = getManagerOptions();

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: C.slate900, letterSpacing: '-.4px' }}>Team Management</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.slate400 }}>
            {users.length} team member{users.length !== 1 ? 's' : ''} &bull; Manage roles, reporting structure, and access.
          </p>
        </div>
        <button onClick={handleCreate} style={{
          padding: '10px 20px', borderRadius: 10, border: 'none', backgroundColor: C.indigo,
          color: C.white, fontSize: 13, fontWeight: 700, cursor: 'pointer',
          boxShadow: '0 2px 10px rgba(99,102,241,.3)',
        }}>+ New User</button>
      </div>

      {/* Role counts */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {ROLE_OPTIONS.map(r => {
          const count = users.filter(u => u.role === r.value && u.is_active).length;
          const rc = roleColors[r.value] || roleColors.pm;
          return (
            <div key={r.value} style={{
              padding: '12px 20px', borderRadius: 12, border: `1px solid ${C.slate200}`,
              backgroundColor: C.white, minWidth: 110,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: C.slate400, marginBottom: 2 }}>
                {r.label}
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, color: rc.fg }}>{count}</div>
            </div>
          );
        })}
      </div>

      {/* Success */}
      {success && (
        <div style={{ padding: '12px 16px', borderRadius: 10, backgroundColor: C.green50, border: '1px solid #bbf7d0', color: C.green600, fontSize: 13, fontWeight: 500, marginBottom: 16 }}>
          {success}
        </div>
      )}

      {/* View Toggle */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: C.slate100, borderRadius: 28, padding: 4, width: 'fit-content' }}>
        <button style={pill(viewMode === 'list')} onClick={() => setViewMode('list')}>List View</button>
        <button style={pill(viewMode === 'org')} onClick={() => setViewMode('org')}>Org Chart</button>
      </div>

      {/* Create / Edit Form */}
      {showForm && (
        <div style={{ ...card, borderColor: C.indigo, borderWidth: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.slate900 }}>
              {editingUser ? `Edit: ${editingUser.first_name} ${editingUser.last_name}` : 'Create New User'}
            </h3>
            <button onClick={resetForm} style={{
              fontSize: 12, fontWeight: 600, color: C.slate500, background: C.slate100,
              border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
            }}>Cancel</button>
          </div>

          {error && (
            <div style={{ padding: '10px 14px', borderRadius: 8, backgroundColor: C.rose50, border: '1px solid #fecaca', color: C.rose600, fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Row 1: Name */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>First Name</label>
                <input type="text" value={formFirstName} onChange={e => setFormFirstName(e.target.value)} required style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Last Name</label>
                <input type="text" value={formLastName} onChange={e => setFormLastName(e.target.value)} required style={inputStyle} />
              </div>
            </div>

            {/* Row 2: Email + Role */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={formEmail} onChange={e => setFormEmail(e.target.value)} required style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Role</label>
                <select value={formRole} onChange={e => { setFormRole(e.target.value); setFormReportsTo(''); }} style={{ ...inputStyle, cursor: 'pointer' }}>
                  {ROLE_OPTIONS.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 3: Reports To + Region */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Reports To</label>
                {formRole === 'admin' ? (
                  <div style={{ ...inputStyle, backgroundColor: C.slate50, color: C.slate400, cursor: 'default' }}>N/A — Admin</div>
                ) : managerOptions.length === 0 ? (
                  <div style={{ ...inputStyle, backgroundColor: C.slate50, color: C.slate400, cursor: 'default' }}>
                    {formRole === 'vp' ? 'N/A — VP reports to no one' : 'No eligible managers found — create one first'}
                  </div>
                ) : (
                  <select value={formReportsTo} onChange={e => setFormReportsTo(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="">None</option>
                    {managerOptions.map(m => (
                      <option key={m.id} value={m.id}>{m.name} ({m.role_label})</option>
                    ))}
                  </select>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Region</label>
                <input type="text" value={formRegion} onChange={e => setFormRegion(e.target.value)} placeholder="e.g., Southeast, Northeast" style={inputStyle} />
              </div>
            </div>

            {/* Row 4: Password + Status */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'flex-end' }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>Password{editingUser ? ' (leave blank to keep current)' : ''}</label>
                <input type="password" value={formPassword} onChange={e => setFormPassword(e.target.value)}
                  required={!editingUser} placeholder={editingUser ? 'Leave blank to keep current' : 'Min. 6 characters'}
                  style={inputStyle} />
              </div>
              {editingUser && (
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Status</label>
                  <select value={formActive ? 'active' : 'inactive'} onChange={e => setFormActive(e.target.value === 'active')}
                    style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="active">Active</option>
                    <option value="inactive">Deactivated</option>
                  </select>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" onClick={resetForm} style={{
                padding: '10px 20px', borderRadius: 10, border: `1px solid ${C.slate200}`,
                backgroundColor: C.white, color: C.slate500, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>Cancel</button>
              <button type="submit" disabled={submitting} style={{
                padding: '10px 24px', borderRadius: 10, border: 'none',
                backgroundColor: C.indigo, color: C.white, fontSize: 13, fontWeight: 700,
                cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1,
              }}>{submitting ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}</button>
            </div>
          </form>
        </div>
      )}

      {/* ═══ LIST VIEW ═══ */}
      {viewMode === 'list' && (
        <div style={card}>
          <div style={{ border: `1px solid ${C.slate200}`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1.2fr 160px 140px 80px 80px 60px',
              padding: '10px 16px', backgroundColor: C.slate50, borderBottom: `2px solid ${C.slate200}`,
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: C.slate500, gap: 8,
            }}>
              <div>Name</div><div>Email</div><div style={{ textAlign: 'center' }}>Role</div>
              <div>Reports To</div><div style={{ textAlign: 'center' }}>Status</div>
              <div style={{ textAlign: 'center' }}>Login</div><div></div>
            </div>

            {users.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: C.slate400, fontSize: 14 }}>
                No users found.
              </div>
            ) : (
              users.map((user, idx) => {
                const rc = roleColors[user.role] || roleColors.pm;
                return (
                  <div key={user.id} style={{
                    display: 'grid', gridTemplateColumns: '1fr 1.2fr 160px 140px 80px 80px 60px',
                    padding: '12px 16px', borderBottom: idx < users.length - 1 ? `1px solid ${C.slate100}` : 'none',
                    alignItems: 'center', gap: 8, fontSize: 13,
                    backgroundColor: !user.is_active ? '#fafafa' : 'transparent',
                    opacity: user.is_active ? 1 : 0.6,
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, color: C.slate900 }}>{user.first_name} {user.last_name}</div>
                      {user.region && <div style={{ fontSize: 11, color: C.slate400 }}>{user.region}</div>}
                    </div>
                    <div style={{ color: C.slate500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user.email}
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 8,
                        backgroundColor: rc.bg, color: rc.fg, whiteSpace: 'nowrap',
                      }}>{roleLabel(user.role)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: C.slate500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user.reports_to_name || '–'}
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 8,
                        backgroundColor: user.is_active ? C.green50 : C.rose50,
                        color: user.is_active ? C.green600 : C.rose600,
                      }}>{user.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                    <div style={{ textAlign: 'center', fontSize: 10, color: C.slate400 }}>
                      {fmtDate(user.last_login)}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <button onClick={() => handleEdit(user)} style={{
                        fontSize: 11, fontWeight: 600, color: C.blue600, background: C.blue50,
                        border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer',
                      }}>Edit</button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ═══ ORG CHART VIEW ═══ */}
      {viewMode === 'org' && (
        <div style={card}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.slate900 }}>Organization Structure</h3>
          {tree.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: C.slate400 }}>
              No org structure defined yet. Set "Reports To" on users to build the tree.
            </div>
          ) : (
            tree.map(node => <OrgNode key={node.id} node={node} depth={0} />)
          )}
        </div>
      )}

      {/* Role Legend */}
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', padding: '12px 0', fontSize: 11, color: C.slate400, flexWrap: 'wrap' }}>
        <span><strong style={{ color: '#7c3aed' }}>Admin</strong> — Full system access</span>
        <span><strong style={{ color: '#b45309' }}>VP</strong> — Sees all projects & people</span>
        <span><strong style={{ color: '#1d4ed8' }}>CD</strong> — Sees their CMs & PMs</span>
        <span><strong style={{ color: C.blue600 }}>CM</strong> — Sees their PMs</span>
        <span><strong style={{ color: C.green600 }}>PM</strong> — Manages assigned projects</span>
      </div>
    </div>
  );
};

export default UserManagementPage;
